import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, LogOut, ShieldCheck, Glasses } from "lucide-react";
import Logo from "@/components/Logo";
import { useLang, useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

function LangToggle() {
  const { lang, setLang, baseLang } = useLang();
  if (!baseLang || baseLang === "en") return null;
  const target = lang === "en" ? baseLang : "en";
  return (
    <button
      data-testid="lang-toggle-en"
      onClick={() => setLang(target)}
      className="rounded-full border border-ink/15 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-ink/60 transition-colors duration-300 hover:border-teal hover:text-teal"
      aria-label="Switch language / Cambiar idioma"
    >
      {target}
    </button>
  );
}

function UserChip({ mobile = false, onAction }) {
  const { t } = useLang();
  const { user, logout } = useAuth();
  if (!user) return null;
  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      data-testid={mobile ? "header-user-chip-mobile" : "header-user-chip"}
      className="inline-flex items-center gap-2.5 rounded-full border border-ink/15 py-1.5 pl-1.5 pr-2"
    >
      {user.picture ? (
        <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full object-cover" referrerPolicy="no-referrer" />
      ) : (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-teal font-mono text-xs font-bold text-white">
          {initial}
        </span>
      )}
      <span className="max-w-[110px] truncate text-xs font-semibold text-ink">{user.name}</span>
      <button
        data-testid={mobile ? "header-logout-btn-mobile" : "header-logout-btn"}
        onClick={() => {
          logout();
          onAction?.();
        }}
        className="rounded-full p-1 text-ink/40 transition-colors duration-200 hover:text-red-500"
        aria-label={t.nav.logout}
        title={t.nav.logout}
      >
        <LogOut className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function Header() {
  const { t } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user?.is_admin) {
      setUnread(0);
      return;
    }
    let stop = false;
    const load = () =>
      fetch(`${API}/api/admin/unread-count`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : { unread: 0 }))
        .then((d) => !stop && setUnread(d.unread || 0))
        .catch(() => {});
    load();
    const id = setInterval(load, 30000);
    const onRead = () => load();
    window.addEventListener("dekrbat:read", onRead);
    return () => {
      stop = true;
      clearInterval(id);
      window.removeEventListener("dekrbat:read", onRead);
    };
  }, [user]);

  const adminLink = user?.is_admin ? (
    <span className="relative inline-flex">
      <Link
        data-testid="header-admin-link"
        to="/admin"
        onClick={() => setOpen(false)}
        className="rounded-full border border-ink/15 p-2 text-ink/60 transition-colors duration-300 hover:border-teal hover:text-teal"
        aria-label={t.admin?.title || "Admin"}
        title={t.admin?.title || "Admin"}
      >
        <ShieldCheck className="h-4 w-4" />
      </Link>
      {unread > 0 && (
        <span
          data-testid="header-admin-badge"
          className="pointer-events-none absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 font-mono text-[9px] font-bold text-white"
        >
          {unread}
        </span>
      )}
    </span>
  ) : null;

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/how-it-works", label: t.nav.how },
    { to: "/licensing", label: t.nav.licensing },
    { to: "/impact", label: t.nav.impact },
    { to: "/pseq2", label: t.nav.pseq2 },
    { to: "/oracle", label: t.nav.oracle },
    { to: "/contact", label: t.nav.contact },
  ];

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-300 ${
      isActive ? "text-teal" : "text-ink/70 hover:text-deep"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-deep/10 bg-bone/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
        <Link to="/" aria-label="Dekrbat Pure" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={linkClass}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LangToggle />
          {adminLink}
          <UserChip />
          <a
            data-testid="nav-xreal-btn"
            href="/pseq2-xr.html"
            className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-ink px-4 py-2.5 font-headline text-xs font-bold text-white shadow-[0_0_16px_-4px_rgba(56,189,248,0.5)] transition-all duration-300 hover:border-sky-400 hover:shadow-[0_0_24px_-4px_rgba(56,189,248,0.7)]"
          >
            <Glasses className="h-4 w-4 text-sky-300" />
            XREAL
          </a>
          <Link
            data-testid="nav-request-license-btn"
            to="/licensing#request-form"
            className="rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-deep"
          >
            {t.nav.cta}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          className="rounded-full border border-ink/15 p-2 text-ink lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? t.nav.close : t.nav.menu}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-deep/10 bg-bone lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-2.5 text-base font-medium ${
                      isActive ? "bg-teal/10 text-teal" : "text-ink/80"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-2 flex flex-wrap items-center gap-3 px-3">
                <LangToggle />
                {adminLink}
                <UserChip mobile onAction={() => setOpen(false)} />
                <a
                  data-testid="nav-xreal-btn-mobile"
                  href="/pseq2-xr.html"
                  className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-ink px-4 py-2.5 font-headline text-xs font-bold text-white"
                >
                  <Glasses className="h-4 w-4 text-sky-300" />
                  XREAL
                </a>
                <Link
                  data-testid="nav-request-license-btn-mobile"
                  to="/licensing#request-form"
                  onClick={() => setOpen(false)}
                  className="rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-white"
                >
                  {t.nav.cta}
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
